import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api, { BACKEND_URL } from '../api/axios';
import AgentLayout from './AgentLayout';
import ActionMenu from './ActionMenu';

const fmtDateTime = (d) =>
  d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const PILL = 'inline-flex items-center justify-center gap-1 whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-bold transition disabled:opacity-50 disabled:cursor-not-allowed';
const PILL_GREEN = `${PILL} bg-[#ECFDF5] text-[#10B981] hover:bg-emerald-100`;
const PILL_TEAL = `${PILL} bg-teal-50 text-teal-600 hover:bg-teal-100`;
const PILL_PURPLE = `${PILL} bg-purple-50 text-[#7C3AED] hover:bg-purple-100`;
const PILL_BLUE = `${PILL} bg-blue-50 text-blue-600 hover:bg-blue-100`;
const PILL_AMBER = `${PILL} bg-amber-50 text-amber-600 hover:bg-amber-100`;
const PILL_GRAY = `${PILL} bg-gray-100 text-gray-600 hover:bg-gray-200`;
const PILL_DISABLED = `${PILL} bg-gray-50 text-gray-300 cursor-default`;
const PILL_GRADIENT = `${PILL} bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-sm hover:opacity-90`;

const FIELD =
  'w-full border border-gray-200 bg-gray-50/60 rounded-xl px-3 py-2.5 text-sm transition focus:outline-none focus:bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const FILTER_LABEL = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1';

const STATUS_STYLES = {
  Scheduled: 'bg-blue-50 text-blue-600',
  Completed: 'bg-emerald-50 text-emerald-600',
  Rescheduled: 'bg-purple-50 text-purple-600',
  Cancelled: 'bg-red-50 text-red-600',
};

const statusOptions = [
  'applied',
  'screening',
  'shortlisted',
  'interview_round_1',
  'interview_round_2',
  'hr_round',
  'offer',
  'joined',
  'rejected',
  'withdrawn',
];

const statusLabels = {
  applied: 'Applied',
  screening: 'Screening',
  shortlisted: 'Shortlisted',
  interview_round_1: 'Interview (Round 1)',
  interview_round_2: 'Interview (Round 2)',
  hr_round: 'HR Round',
  offer: 'Offer',
  joined: 'Joined',
  rejected: 'Rejected',
    withdrawn: 'Withdrawn',
};

const APPLICATION_STATUS_STYLES = {
  applied: 'bg-gray-50 text-gray-600',
  screening: 'bg-blue-50 text-blue-600',
  shortlisted: 'bg-teal-50 text-teal-600',
  interview_round_1: 'bg-amber-50 text-amber-600',
  interview_round_2: 'bg-amber-50 text-amber-600',
  hr_round: 'bg-amber-50 text-amber-600',
  offer: 'bg-emerald-50 text-emerald-600',
  joined: 'bg-emerald-50 text-emerald-600',
  rejected: 'bg-red-50 text-red-600',
    withdrawn: 'bg-gray-100 text-gray-500',
};

const INTERVIEW_TYPES = ['Telephonic', 'Video', 'In-Person', 'Technical', 'HR Round'];

const rowKey = (a) => `${a.candidate_id}-${a.job_posting_id}`;

// Searchable dropdown — used for Company and Job filters.
// options: [{ value, label }]. "allLabel" acts as a clear/reset option.
function SearchableCombobox({ value, onChange, options, placeholder, allLabel, disabled }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label || '';

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    if (!open || !query) return options;
    return options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()));
  }, [options, query, open]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={open ? query : selectedLabel}
        disabled={disabled}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onClick={() => { if (!disabled) { setOpen(true); } }}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        className={`w-full border border-gray-200 rounded-xl pl-3 pr-8 py-2.5 text-sm transition focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}`}
      />
      {!disabled && (
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
      )}
      {!disabled && open && (
        <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          <button
            type="button"
            onClick={() => { onChange(''); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-purple-50 hover:text-purple-600"
          >
            {allLabel}
          </button>
          {filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-400">No matches</div>
          )}
          {filtered.map((o) => (
            <button
              type="button"
              key={o.value}
              onClick={() => { onChange(o.value); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Free-text input with suggestions — used for the City filter.
function CitySearchInput({ value, onChange, suggestions, placeholder }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = useMemo(() => {
    const base = value
      ? suggestions.filter((c) => c.toLowerCase().includes(value.toLowerCase()))
      : suggestions;
    return base.slice(0, 8);
  }, [suggestions, value]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        className={FIELD}
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          {filtered.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => { onChange(c); setOpen(false); }}
              className="block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600"
            >
              {c}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}


function MultiSelect({ value, onChange, options, allLabel }) {
  // options: [{ value, label }]
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => o.label.toLowerCase().includes(s)) : options;
  }, [options, q]);

  const toggle = (v) =>
    onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);

  const label =
    value.length === 0
      ? allLabel
      : value.length === 1
      ? options.find((o) => o.value === value[0])?.label || value[0]
      : `${value.length} selected`;

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${FIELD} text-left truncate cursor-pointer pr-8`}
      >
        {label}
      </button>
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[220px] bg-white border border-gray-100 rounded-xl shadow-lg">
          <div className="p-2 border-b border-gray-100">
            <input
              type="text"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search..."
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {value.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="block w-full text-left px-3 py-2 text-sm font-semibold text-pink-600 hover:bg-purple-50"
              >
                Clear selection
              </button>
            )}
            {filtered.map((o) => (
              <label
                key={o.value}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-purple-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={value.includes(o.value)}
                  onChange={() => toggle(o.value)}
                  className="h-4 w-4 rounded border-gray-300 accent-purple-600"
                />
                <span className="truncate">{o.label}</span>
              </label>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-2 text-xs text-gray-400">No matches</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AgentInterviews() {
  const [employers, setEmployers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applicants, setApplicants] = useState([]);
  const [interviews, setInterviews] = useState([]);

  const [selectedEmployers, setSelectedEmployers] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);

  const [loadingEmployers, setLoadingEmployers] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingApplicants, setLoadingApplicants] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [activeRow, setActiveRow] = useState(null);
  const [rowForm, setRowForm] = useState({ type: 'Telephonic', scheduled_at: '' });
  const [saving, setSaving] = useState(false);
  const [rowError, setRowError] = useState('');

  const [feedbackRow, setFeedbackRow] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');

  const [search, setSearch] = useState('');
  const [viewCandidate, setViewCandidate] = useState(null);
    const [reasonApplicant, setReasonApplicant] = useState(null);

    const [profileUser, setProfileUser] = useState(null);
  const [employerProfile, setEmployerProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState([]);
  const [cityFilter, setCityFilter] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);


    const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [search, dateFilter, statusFilter, cityFilter, selectedEmployers, selectedJobs, pageSize]);

  const availableJobs = useMemo(
    () =>
      selectedEmployers.length === 0
        ? jobs
        : jobs.filter((j) => selectedEmployers.includes(j.employer_id)),
    [jobs, selectedEmployers]
  );
    const companyOptions = useMemo(() => {
    const map = new Map();
    employers.forEach((e) => map.set(String(e.id), { value: e.id, label: e.name }));
    jobs.forEach((j) => {
      if (j.employer_id && !map.has(String(j.employer_id))) {
        map.set(String(j.employer_id), {
          value: j.employer_id,
          label: j.employer_name || `Company #${j.employer_id}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [employers, jobs]);

  const effectiveJobIds = useMemo(
    () => (selectedJobs.length === 0 ? availableJobs.map((j) => j.id) : selectedJobs),
    [availableJobs, selectedJobs]
  );
  const isAllJobs = effectiveJobIds.length !== 1;
  const colCount = (isAllJobs ? 10 : 9) + 1; // +1 for checkbox, +1 for Company column

  const cityFor = useCallback((a) => {
    const job = jobs.find((j) => String(j.id) === String(a.job_posting_id));
    return job?.location || a.location || '';
  }, [jobs]);

  const knownCities = useMemo(
    () => Array.from(new Set(availableJobs.map((j) => j.location).filter(Boolean))).sort(),
    [availableJobs]
  );

  const filteredApplicants = applicants.filter((a) => {
    const q = search.trim().toLowerCase();
    const matchesSearch = !q || (
      a.candidate_name?.toLowerCase().includes(q) ||
      a.candidate_phone?.toLowerCase().includes(q) ||
      (a.job_title || jobs.find((j) => String(j.id) === String(a.job_posting_id))?.title || '')
        .toLowerCase()
        .includes(q)
    );
    const matchesDate = !dateFilter || (a.applied_at && a.applied_at.slice(0, 10) === dateFilter);
    const matchesStatus =
      statusFilter.length === 0 || statusFilter.includes(a.application_status || 'applied');
    const matchesCity = cityFilter.length === 0 || cityFilter.includes(cityFor(a));
    return matchesSearch && matchesDate && matchesStatus && matchesCity;
  });


    const totalPages = Math.max(1, Math.ceil(filteredApplicants.length / pageSize));
  const curPage = Math.min(page, totalPages);
  const pagedApplicants = filteredApplicants.slice((curPage - 1) * pageSize, curPage * pageSize);

  // Step 1: load employers on mount
  useEffect(() => {
    (async () => {
      setLoadingEmployers(true);
      try {
        const res = await api.get('/agent/employers');
        setEmployers(res.data);
      } catch (err) {
        console.error('Failed to load employers', err);
        setLoadError('Could not load companies.');
      } finally {
        setLoadingEmployers(false);
      }
    })();
  }, []);



 
  // Step 2: load all jobs once (company/job filters work in the browser)
  useEffect(() => {
    (async () => {
      setLoadingJobs(true);
      try {
        const res = await api.get('/agent/jobs');
        setJobs(res.data);
      } catch (err) {
        console.error('Failed to load jobs', err);
        setLoadError('Could not load jobs.');
      } finally {
        setLoadingJobs(false);
      }
    })();
  }, []);

  // Step 3: load applicants + interviews for the selected jobs
  const loadApplicantsAndInterviews = useCallback(async () => {
    if (loadingJobs) return;
    setLoadingApplicants(true);
    setLoadError('');
    setSelectedIds(new Set());
    try {
      const [batchRes, iRes] = await Promise.all([
        effectiveJobIds.length
          ? api.post('/agent/job-applicants-batch', { job_posting_ids: effectiveJobIds })
          : Promise.resolve({ data: [] }),
        api.get('/agent/interviews'),
      ]);
      setApplicants(batchRes.data);
      setInterviews(iRes.data);
    } catch (err) {
      console.error('Failed to load applicants', err);
      setLoadError('Could not load applicants.');
    } finally {
      setLoadingApplicants(false);
    }
  }, [effectiveJobIds, loadingJobs]);

  useEffect(() => {
    loadApplicantsAndInterviews();
  }, [loadApplicantsAndInterviews]);

  const interviewFor = (candidateId, jobPostingId) => {
    const matches = interviews.filter(
      (i) => i.candidate_id === candidateId && String(i.job_posting_id) === String(jobPostingId)
    );
    if (matches.length === 0) return null;
    return matches.reduce((latest, i) =>
      new Date(i.scheduled_at) > new Date(latest.scheduled_at) ? i : latest
    );
  };

  const companyFor = (a) => {
    const job = jobs.find((j) => String(j.id) === String(a.job_posting_id));
    return (
      a.employer_name ||
      job?.employer_name ||
      employers.find((e) => String(e.id) === String(job?.employer_id))?.name ||
      '—'
    );
  };

    const employerIdFor = (a) => {
    const job = jobs.find((j) => String(j.id) === String(a.job_posting_id));
    return job?.employer_id || null;
  };

  const openCandidateProfile = async (id) => {
    setProfileLoading(true);
    try {
      const res = await api.get(`/agent/candidates/${id}/profile`);
      setProfileUser(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load candidate profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  const openEmployerProfile = async (id) => {
    if (!id) return;
    setProfileLoading(true);
    try {
      const res = await api.get(`/agent/employers/${id}/profile`);
      setEmployerProfile(res.data);
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to load company profile.');
    } finally {
      setProfileLoading(false);
    }
  };

  const openScheduleRow = (a) => {
    setActiveRow(rowKey(a));
    setRowForm({ type: 'Telephonic', scheduled_at: '' });
    setRowError('');
  };

  const saveSchedule = async (a) => {
    if (!rowForm.scheduled_at) {
      setRowError('Pick a date & time.');
      return;
    }
    setSaving(true);
    setRowError('');
    try {
      await api.post('/agent/interviews', {
        candidate_id: a.candidate_id,
        job_posting_id: a.job_posting_id,
        type: rowForm.type,
        scheduled_at: rowForm.scheduled_at,
      });
      setActiveRow(null);
      loadApplicantsAndInterviews();
    } catch (err) {
      const data = err.response?.data;
      setRowError(data?.message || 'Could not schedule interview.');
    } finally {
      setSaving(false);
    }
  };

  const markStatus = async (id, status) => {
    try {
      await api.put(`/agent/interviews/${id}/status`, { status });
      loadApplicantsAndInterviews();
    } catch (err) {
      console.error('Failed to update status', err);
    }
  };

  const openFeedback = (iv) => {
    setFeedbackRow(iv.id);
    setFeedbackText(iv.feedback || '');
  };

  const saveFeedback = async (id) => {
    try {
      await api.put(`/agent/interviews/${id}/feedback`, { feedback: feedbackText });
      setFeedbackRow(null);
      loadApplicantsAndInterviews();
    } catch (err) {
      console.error('Failed to save feedback', err);
    }
  };

  const handleStatusChange = async (applicationId, newStatus) => {
     if (!applicationId) {
    console.error('Missing application_id for status update');
    return;
  }
    setApplicants((prev) =>
      prev.map((a) =>
        a.application_id === applicationId ? { ...a, application_status: newStatus } : a
      )
    );
    try {
      await api.put(`/agent/job-applications/${applicationId}/status`, { status: newStatus });
    } catch (err) {
      console.error('Failed to update status', err);
      loadApplicantsAndInterviews();
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      prev.size === filteredApplicants.length
        ? new Set()
        : new Set(filteredApplicants.map((a) => a.application_id))
    );
  };

  const handleBulkStatus = async (status) => {
    if (selectedIds.size === 0) return;
    const label = status === 'shortlisted' ? 'shortlist' : 'reject';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} ${selectedIds.size} selected applicant(s)?`)) return;

    setBulkLoading(true);
    const ids = Array.from(selectedIds);
    setApplicants((prev) =>
      prev.map((a) => (ids.includes(a.application_id) ? { ...a, application_status: status } : a))
    );
    try {
      await Promise.all(ids.map((id) => api.put(`/agent/job-applications/${id}/status`, { status })));
      setSelectedIds(new Set());
    } catch (err) {
      console.error(`Failed to bulk ${label}`, err);
      loadApplicantsAndInterviews();
    } finally {
      setBulkLoading(false);
    }
  };

  const hasExtraFilters = dateFilter || statusFilter.length > 0 || cityFilter.length > 0;
  const clearExtraFilters = () => {
    setDateFilter('');
    setStatusFilter([]);
    setCityFilter([]);
  };

  return (
    <AgentLayout>
      <div className="bg-gradient-to-br from-[#FAF5FC] to-[#FDF1F9] -m-6 p-6 min-h-screen">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
        Applications
        </h2>
      </div>
      <p className="text-gray-500 text-sm mb-6">
        Pick a company and job to see who applied, then schedule interviews.
      </p>

            {!loadingApplicants && applicants.length > 0 && (
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 min-w-[140px]">
            <p className="text-xs text-gray-400">Total Applicants</p>
            <p className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
              {applicants.length}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 min-w-[140px]">
            <p className="text-xs text-gray-400">Scheduled</p>
            <p className="text-lg font-bold text-blue-600">
              {applicants.filter((a) => {
                const iv = interviewFor(a.candidate_id, a.job_posting_id);
                return iv && iv.status === 'Scheduled';
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 min-w-[140px]">
            <p className="text-xs text-gray-400">Completed</p>
            <p className="text-lg font-bold text-emerald-600">
              {applicants.filter((a) => {
                const iv = interviewFor(a.candidate_id, a.job_posting_id);
                return iv && iv.status === 'Completed';
              }).length}
            </p>
          </div>
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 min-w-[140px]">
            <p className="text-xs text-gray-400">Not Scheduled</p>
            <p className="text-lg font-bold text-gray-500">
              {applicants.filter((a) => !interviewFor(a.candidate_id, a.job_posting_id)).length}
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Company</label>
            <MultiSelect
              value={selectedEmployers}
              onChange={(v) => { setSelectedEmployers(v); setSelectedJobs([]); }}
              options={companyOptions}
              allLabel={loadingEmployers ? 'Loading…' : 'All Companies'}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">Job</label>
            <MultiSelect
              value={selectedJobs}
              onChange={setSelectedJobs}
              options={availableJobs.map((j) => ({
                value: j.id,
                label: selectedEmployers.length === 1 || !j.employer_name
                  ? j.title
                  : `${j.title} · ${j.employer_name}`,
              }))}
              allLabel={loadingJobs ? 'Loading…' : 'All Jobs'}
            />
          </div>
        </div>
      </div>

      {loadError && (
        <div className="bg-red-50 text-red-600 text-sm rounded-xl p-3 mb-4 border border-red-100">{loadError}</div>
      )}



   {!loadingApplicants && applicants.length > 0 && (
  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-4">
   
   
    {/* Result count + clear */}
    <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
      <span>
        Showing <b className="text-purple-700">{filteredApplicants.length}</b> of {applicants.length} applicants
      </span>
      {(hasExtraFilters || search) && (
        <button
          type="button"
          onClick={() => { setSearch(''); clearExtraFilters(); }}
          className="font-semibold text-purple-600 hover:underline"
        >
          Clear all
        </button>
      )}
    </div>

    {/* Search */}
    <div className="relative mb-4">
      <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by candidate name, mobile or job..."
        className={`${FIELD} pl-10 pr-10`}
      />
      {search && (
        <button
          type="button"
          onClick={() => setSearch('')}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 text-lg leading-none"
          aria-label="Clear search"
        >
          ×
        </button>
      )}
    </div>

    {/* Filters */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <div>
        <label className={FILTER_LABEL}>City</label>
        <MultiSelect
          value={cityFilter}
          onChange={setCityFilter}
          options={knownCities.map((c) => ({ value: c, label: c }))}
          allLabel="All Cities"
        />
      </div>
      <div>
        <label className={FILTER_LABEL}>Applied On</label>
        <input
          type="date"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className={FIELD}
        />
      </div>
      <div>
        <label className={FILTER_LABEL}>Status</label>
        <MultiSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={statusOptions.map((s) => ({ value: s, label: statusLabels[s] }))}
          allLabel="All Statuses"
        />
      </div>
    </div>

    
  </div>
)}

      {!loadingApplicants && applicants.length > 0 && (
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2 text-sm text-gray-600">
          <span className="font-semibold">
            Showing {filteredApplicants.length === 0 ? 0 : (curPage - 1) * pageSize + 1}–{Math.min(curPage * pageSize, filteredApplicants.length)} of {filteredApplicants.length}
          </span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="border border-purple-200 rounded-full px-2 py-1 text-xs bg-white"
          >
            {[10, 25, 50, 100].map((n) => (
              <option key={n} value={n}>{n} / page</option>
            ))}
          </select>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 flex-wrap mb-4 bg-purple-50 border border-purple-100 rounded-xl px-4 py-2.5">
          <span className="text-xs font-semibold text-gray-600">{selectedIds.size} selected</span>
          <button
            onClick={() => handleBulkStatus('shortlisted')}
            disabled={bulkLoading}
            className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:opacity-90 disabled:opacity-50 shadow-sm"
          >
            Shortlist
          </button>
          <button
            onClick={() => handleBulkStatus('rejected')}
            disabled={bulkLoading}
            className="text-xs font-semibold px-3.5 py-1.5 rounded-full bg-gradient-to-r from-red-500 to-rose-500 text-white hover:opacity-90 disabled:opacity-50 shadow-sm"
          >
            Reject
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            disabled={bulkLoading}
            className="text-xs font-semibold px-3.5 py-1.5 rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      )}

      {(
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-x-auto">
          <table className="w-full text-sm min-w-[1500px]">
            <thead>
              <tr className="text-left text-gray-400 text-[10px] uppercase border-b border-gray-100 bg-gray-50/60">
                <th className="px-5 py-3 font-semibold w-8">
                  {filteredApplicants.length > 0 && (
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filteredApplicants.length}
                      onChange={toggleSelectAll}
                      className="accent-purple-600"
                    />
                  )}
                </th>
                <th className="px-5 py-3 font-semibold">Candidate</th>
                <th className="px-5 py-3 font-semibold">Company</th>
                {isAllJobs && <th className="px-5 py-3 font-semibold">Job</th>}
                <th className="px-5 py-3 font-semibold">Mobile</th>
                <th className="px-5 py-3 font-semibold">Applied</th>
                <th className="px-5 py-3 font-semibold">Profile</th>
                <th className="px-5 py-3 font-semibold">Interview</th>
                <th className="px-5 py-3 font-semibold">Int. Status</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold text-right sticky right-0 z-20 bg-gray-50 border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">Action</th>
              </tr>
            </thead>
            <tbody>
              {(loadingApplicants || loadingJobs) && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">Loading applicants…</td></tr>
              )}
              {!loadingApplicants && !loadingJobs && applicants.length === 0 && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">No one has applied to this job yet.</td></tr>
              )}
              {!loadingApplicants && applicants.length > 0 && filteredApplicants.length === 0 && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">No applicants match your search.</td></tr>
              )}
              {!loadingApplicants && pagedApplicants.map((a) => {
                const iv = interviewFor(a.candidate_id, a.job_posting_id);
                const key = rowKey(a);
                const isScheduling = activeRow === key;
                const isFeedback = feedbackRow === iv?.id;

                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                      <td className="px-5 py-3">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(a.application_id)}
                          onChange={() => toggleSelect(a.application_id)}
                          className="accent-purple-600"
                        />
                      </td>
                      <td className="px-5 py-3 max-w-[170px] truncate font-medium">
                        <button
                          type="button"
                          onClick={() => openCandidateProfile(a.candidate_id)}
                          className="text-purple-600 hover:underline truncate max-w-full"
                          title={a.candidate_name}
                        >
                          {a.candidate_name}
                        </button>
                      </td>
                      <td className="px-5 py-3 	max-w-[170px] truncate">
                        {employerIdFor(a) ? (
                          <button
                            type="button"
                            onClick={() => openEmployerProfile(employerIdFor(a))}
                            className="text-purple-600 hover:underline truncate max-w-full"
                            title={companyFor(a)}
                          >
                            {companyFor(a)}
                          </button>
                        ) : companyFor(a)}
                      </td>
                      {isAllJobs && (
                        <td className="px-5 py-3 	max-w-[170px] truncate">
                          {a.job_title || jobs.find((j) => String(j.id) === String(a.job_posting_id))?.title || '—'}
                        </td>
                      )}
                      <td className="px-5 py-3 whitespace-nowrap">{a.candidate_phone || '—'}</td>
                      <td className="px-5 py-3 whitespace-nowrap">{fmtDateTime(a.applied_at)}</td>
                      <td className="px-5 py-3">
                        <button
                          onClick={() => setViewCandidate(a)}
                          className="text-purple-600 hover:underline text-xs font-semibold"
                        >
                          View
                        </button>
                      </td>
                      <td className="px-5 py-3 whitespace-nowrap">{iv ? `${iv.type} · ${fmtDateTime(iv.scheduled_at)}` : '—'}</td>
                      <td className="px-5 py-3">
                        {iv ? (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[iv.status] || 'bg-gray-50 text-gray-500'}`}>
                            {iv.status}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400 whitespace-nowrap">Not scheduled</span>
                        )}
                      </td>
                                         <td className="px-5 py-3">
                        <select
                          value={a.application_status || 'applied'}
                          onChange={(e) => handleStatusChange(a.application_id, e.target.value)}
                          className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap border-0 cursor-pointer ${APPLICATION_STATUS_STYLES[a.application_status] || 'bg-gray-50 text-gray-500'}`}
                        >
                          {statusOptions.map((s) => (
                            <option key={s} value={s}>{statusLabels[s]}</option>
                          ))}
                        </select>
                        {a.application_status === 'withdrawn' && a.withdraw_reason && (
                          <button
                            type="button"
                            onClick={() => setReasonApplicant(a)}
                            className="mt-1 block text-[10px] font-semibold text-purple-600 hover:underline"
                          >
                            View reason
                          </button>
                        )}
                      </td>

                     <td className="px-5 py-3 sticky right-0 z-10 bg-white border-l border-gray-100 shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)] text-right">
  <ActionMenu
    phone={a.candidate_phone}
    items={[
      iv && iv.status !== 'Completed' && iv.status !== 'Cancelled' && {
        label: 'Complete', icon: '✓', tone: 'green', onClick: () => markStatus(iv.id, 'Completed'),
      },
      iv && { label: 'Feedback', icon: '📝', tone: 'amber', onClick: () => openFeedback(iv) },
      {
        label: isScheduling ? 'Cancel schedule' : iv ? 'Reschedule' : 'Schedule',
        icon: '📅',
        tone: isScheduling ? 'gray' : 'purple',
        onClick: () => (isScheduling ? setActiveRow(null) : openScheduleRow(a)),
      },
    ]}
  />
</td>
                    </tr>
                                        {isScheduling && (
                      <tr className="bg-purple-50/40 border-t border-gray-50">
                        <td colSpan={colCount} className="px-5 py-4">
                          <div className="flex flex-wrap items-end gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Type</label>
                              <select
                                value={rowForm.type}
                                onChange={(e) => setRowForm({ ...rowForm, type: e.target.value })}
                                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                              >
                                {INTERVIEW_TYPES.map((t) => (
                                  <option key={t} value={t}>{t}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">Date & Time</label>
                              <input
                                type="datetime-local"
                                value={rowForm.scheduled_at}
                                onChange={(e) => setRowForm({ ...rowForm, scheduled_at: e.target.value })}
                                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                              />
                            </div>
                            <button
                              onClick={() => saveSchedule(a)}
                              disabled={saving}
                              className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2 rounded-xl text-sm font-semibold shadow-sm hover:opacity-90 disabled:opacity-50"
                            >
                              {saving ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                          {rowError && <p className="text-red-500 text-xs mt-2">{rowError}</p>}
                        </td>
                      </tr>
                    )}

                    {isFeedback && (
                      <tr className="bg-amber-50/40 border-t border-gray-50">
                        <td colSpan={colCount} className="px-5 py-4">
                          <label className="block text-xs font-semibold text-gray-600 mb-1">Interview Feedback</label>
                          <textarea
                            value={feedbackText}
                            onChange={(e) => setFeedbackText(e.target.value)}
                            className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                            rows={3}
                          />
                          <div className="mt-2 space-x-2">
                            <button onClick={() => saveFeedback(iv.id)} className="bg-gradient-to-r from-purple-600 to-pink-500 text-white px-4 py-1.5 rounded-xl text-sm font-semibold shadow-sm hover:opacity-90">
                              Save Feedback
                            </button>
                            <button onClick={() => setFeedbackRow(null)} className="text-gray-500 text-sm hover:underline">
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!loadingApplicants && applicants.length > 0 && (() => {
        const total = filteredApplicants.length;
        const from = total === 0 ? 0 : (curPage - 1) * pageSize + 1;
        const to = Math.min(curPage * pageSize, total);
        const nums = [];
        for (let p = Math.max(1, curPage - 2); p <= Math.min(totalPages, curPage + 2); p++) nums.push(p);
        const btn = 'px-3 py-1.5 rounded-full text-xs font-semibold border disabled:opacity-40';
        return (
          <div className="flex items-center justify-between flex-wrap gap-3 mt-3 text-sm text-gray-600">
            <span>Showing {from}–{to} of {total}</span>
            <div className="flex items-center gap-1">
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(1)}>«</button>
              <button className={btn} disabled={curPage === 1} onClick={() => setPage(curPage - 1)}>Prev</button>
              {nums.map((p) => (
                <button
                  key={p}
                  onClick={() => setPage(p)}
                  className={`${btn} ${p === curPage ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white border-transparent' : 'bg-white border-purple-200'}`}
                >
                  {p}
                </button>
              ))}
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(curPage + 1)}>Next</button>
              <button className={btn} disabled={curPage === totalPages} onClick={() => setPage(totalPages)}>»</button>
            </div>
          </div>
        );
      })()}
      
      {profileUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setProfileUser(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setProfileUser(null)} className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold">×</button>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                {profileUser.profile_photo_path && (
                  <img src={`${BACKEND_URL}/storage/${profileUser.profile_photo_path}`} alt={profileUser.name} className="w-16 h-16 rounded-full object-cover" />
                )}
                <div>
                  <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{profileUser.name}</h3>
                  <p className="text-gray-500 text-sm">{profileUser.email}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {profileUser.phone || '—'}</p>
                <p><span className="font-medium">DOB:</span> {profileUser.date_of_birth || '—'}</p>
                <p><span className="font-medium">Gender:</span> {profileUser.gender || '—'}</p>
                <p><span className="font-medium">City:</span> {profileUser.city || '—'}</p>
                <p><span className="font-medium">State:</span> {profileUser.state || '—'}</p>
                <p><span className="font-medium">Pincode:</span> {profileUser.pincode || '—'}</p>
                <p className="col-span-2"><span className="font-medium">Address:</span> {profileUser.address || '—'}</p>
              </div>
              {profileUser.career_objective && (
                <p className="text-sm text-gray-700"><span className="font-medium">Career Objective:</span> {profileUser.career_objective}</p>
              )}
              {profileUser.resume_path && (
                <a href={`${BACKEND_URL}/storage/${profileUser.resume_path}`} target="_blank" rel="noreferrer" className="inline-block text-purple-600 hover:underline text-sm font-medium">View Resume</a>
              )}
              {profileUser.skills?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Skills</h4>
                  <div className="flex flex-wrap gap-2">
                    {profileUser.skills.map((s) => (
                      <span key={s.id} className="bg-purple-50 text-purple-600 text-xs font-medium px-2 py-1 rounded">{s.skill_name || s.name || s.skill || s.title} {s.proficiency ? `(${s.proficiency})` : ''}</span>
                    ))}
                  </div>
                </div>
              )}
              {profileUser.educations?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Education</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.educations.map((e) => (
                      <li key={e.id}>{e.degree} — {e.institution} {e.year_of_passing ? `(${e.year_of_passing})` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {profileUser.experiences?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Experience</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.experiences.map((exp) => (
                      <li key={exp.id}>{exp.job_title} — {exp.company_name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {profileUser.job_applications?.length > 0 && (
                <div>
                  <h4 className="font-bold text-gray-800 mb-2">Job Applications</h4>
                  <ul className="text-sm text-gray-700 space-y-1">
                    {profileUser.job_applications.map((app) => (
                      <li key={app.id} className="border-b pb-1">
                        <span className="font-medium">{app.job_posting?.title}</span> at {app.job_posting?.company_name} — <span className="capitalize">{app.status}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {employerProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" onClick={() => setEmployerProfile(null)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 relative" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setEmployerProfile(null)} className="absolute top-4 right-4 text-gray-400 hover:text-purple-600 text-xl font-bold">×</button>
            <div className="space-y-5">
              <div>
                <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">{employerProfile.profile.name}</h3>
                <p className="text-gray-500 text-sm">{employerProfile.profile.email}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                <p><span className="font-medium">Phone:</span> {employerProfile.profile.phone || '—'}</p>
                <p><span className="font-medium">City:</span> {employerProfile.profile.city || '—'}</p>
                <p><span className="font-medium">State:</span> {employerProfile.profile.state || '—'}</p>
                <p><span className="font-medium">Website:</span> {employerProfile.profile.company_website || '—'}</p>
                <p><span className="font-medium">GST:</span> {employerProfile.profile.company_gst || '—'}</p>
                <p><span className="font-medium">Size:</span> {employerProfile.profile.company_size || '—'}</p>
                <p><span className="font-medium">Industry:</span> {employerProfile.profile.company_industry || '—'}</p>
                <p><span className="font-medium">Verified:</span> {employerProfile.profile.company_verified_status || 'unverified'}</p>
              </div>
              {employerProfile.profile.company_about && (
                <p className="text-sm text-gray-700"><span className="font-medium">About:</span> {employerProfile.profile.company_about}</p>
              )}
              <div>
                <h4 className="font-bold text-gray-800 mb-2">Job Postings</h4>
                {employerProfile.jobs.length === 0 ? (
                  <p className="text-sm text-gray-400">No jobs posted.</p>
                ) : (
                  <ul className="text-sm text-gray-700 space-y-1">
                    {employerProfile.jobs.map((j) => (
                      <li key={j.id} className="border-b pb-1">
                        <span className="font-medium">{j.title}</span> · {j.location || '—'} · <span className="capitalize">{j.status}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

            {reasonApplicant && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setReasonApplicant(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
                Withdraw Reason
              </h3>
              <button
                onClick={() => setReasonApplicant(null)}
                className="text-gray-400 hover:text-purple-600 text-xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Candidate:</span> {reasonApplicant.candidate_name}</p>
              <p>
                <span className="text-gray-400">Job:</span>{' '}
                {reasonApplicant.job_title || jobs.find((j) => String(j.id) === String(reasonApplicant.job_posting_id))?.title || '—'}
              </p>
              <p className="whitespace-pre-wrap break-words">
                <span className="text-gray-400">Reason:</span> {reasonApplicant.withdraw_reason}
              </p>
              <p>
                <span className="text-gray-400">Withdrawn on:</span>{' '}
                {reasonApplicant.withdrawn_at
                  ? new Date(reasonApplicant.withdrawn_at).toLocaleString('en-IN', {
                      day: '2-digit',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '—'}
              </p>
            </div>
          </div>
        </div>
      )}

      {viewCandidate && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
          onClick={() => setViewCandidate(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
                {viewCandidate.candidate_name}
              </h3>
              <button onClick={() => setViewCandidate(null)} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
            </div>
            <div className="space-y-2 text-sm">
              <p><span className="text-gray-400">Mobile:</span> {viewCandidate.candidate_phone || '—'}</p>
              <p><span className="text-gray-400">Company:</span> {companyFor(viewCandidate)}</p>
              <p>
                <span className="text-gray-400">Job Applied:</span>{' '}
                {viewCandidate.job_title || jobs.find((j) => String(j.id) === String(viewCandidate.job_posting_id))?.title || '—'}
              </p>
              <p>
                <span className="text-gray-400">Application Status:</span>{' '}
                <select
                  value={viewCandidate.application_status || 'applied'}
                  onChange={(e) => {
                    handleStatusChange(viewCandidate.application_id, e.target.value);
                    setViewCandidate({ ...viewCandidate, application_status: e.target.value });
                  }}
                  className={`text-xs font-semibold px-2 py-1 rounded-full border-0 cursor-pointer ${APPLICATION_STATUS_STYLES[viewCandidate.application_status] || 'bg-gray-50 text-gray-500'}`}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>{statusLabels[s]}</option>
                  ))}
                </select>
              </p>
                            {viewCandidate.application_status === 'withdrawn' && viewCandidate.withdraw_reason && (
                <p>
                  <span className="text-gray-400">Withdraw Reason:</span> {viewCandidate.withdraw_reason}
                </p>
              )}

              <p><span className="text-gray-400">Applied On:</span> {fmtDateTime(viewCandidate.applied_at)}</p>
              {(() => {
                const iv = interviewFor(viewCandidate.candidate_id, viewCandidate.job_posting_id);
                return iv ? (
                  <>
                    <p><span className="text-gray-400">Interview:</span> {iv.type} · {fmtDateTime(iv.scheduled_at)}</p>
                    <p><span className="text-gray-400">Interview Status:</span> {iv.status}</p>
                    {iv.feedback && <p><span className="text-gray-400">Feedback:</span> {iv.feedback}</p>}
                  </>
                ) : (
                  <p><span className="text-gray-400">Interview:</span> Not scheduled</p>
                );
              })()}
              <p>
                <span className="text-gray-400">Resume:</span>{' '}
                {viewCandidate.resume_path ? (
                  <a
                    href={`${BACKEND_URL}/storage/${viewCandidate.resume_path}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:underline font-semibold"
                  >
                    View Resume
                  </a>
                ) : '—'}
              </p>
            </div>
          </div>
        </div>
      )}
      </div>
    </AgentLayout>
  );
}

export default AgentInterviews;